import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Building2, Store, Building, School, HeartPulse, Warehouse, CalendarCheck, Clock, FileText, ShieldCheck, Users, TrendingUp, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import CallbackForm from "../components/CallbackForm";
import Reviews from "../components/Reviews";
import Contact from "../components/Contact";
import { serviceIndex } from "../data/services";
import { companyInfo } from "../data/mock";

const SITE_URL = "https://www.clevencleaning.nl";
const URL = `${SITE_URL}/zakelijk`;

const audiences = [
  { icon: Building2, title: "Kantoren", text: "Schone ramen en een frisse entree voor medewerkers en bezoekers." },
  { icon: Store, title: "Winkels & horeca", text: "Etalages en puien die uitnodigen, gewassen buiten uw openingstijden." },
  { icon: Building, title: "VvE's & appartementen", text: "Galerijen, entrees en gemeenschappelijk glas op een vast schema, met één factuur voor de VvE." },
  { icon: School, title: "Scholen & kinderopvang", text: "Gepland in vakanties of buiten lestijden, veilig en zonder overlast." },
  { icon: HeartPulse, title: "Zorginstellingen", text: "Rustige, discrete werkwijze met vaste, herkenbare medewerkers." },
  { icon: Warehouse, title: "Bedrijfspanden & showrooms", text: "Grote glaspuien, gevels en zonnepanelen op hoogte tot 12 meter." },
];

const reasons = [
  { icon: CalendarCheck, title: "Vaste planning", text: "Een schema dat past bij uw pand. Wij plannen automatisch door en herinneren u vooraf." },
  { icon: Clock, title: "Buiten openingstijden", text: "Vroeg in de ochtend, 's avonds of in het weekend: wij werken wanneer het u uitkomt." },
  { icon: FileText, title: "Verzamelfactuur", text: "Eén duidelijke factuur per maand of kwartaal, met btw-specificatie voor uw administratie." },
  { icon: ShieldCheck, title: "Verzekerd & veilig", text: "Volledig verzekerd voor bedrijfsaansprakelijkheid. Wij werken tot 12 meter zonder ladders." },
  { icon: Users, title: "Eén aanspreekpunt", text: "Direct contact met Felix. Geen callcenter, wel snelle antwoorden." },
  { icon: TrendingUp, title: "Flexibel opschalen", text: "Extra panden, meer frequentie of een eenmalige grote beurt? Eén berichtje is genoeg." },
];

export default function BusinessPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${URL}#service`,
        serviceType: "Zakelijke glasbewassing en schoonmaak",
        name: "Zakelijke glasbewassing Rosmalen & Den Bosch",
        description: "Glasbewassing, gevelreiniging en periodiek onderhoud voor kantoren, winkels, VvE's, scholen en zorginstellingen in de regio 's-Hertogenbosch.",
        url: URL,
        audience: { "@type": "BusinessAudience", name: "Bedrijven, VvE's en instellingen" },
        provider: { "@type": "LocalBusiness", name: "Cleven Cleaning", telephone: "+31683653511", url: SITE_URL },
        areaServed: { "@type": "AdministrativeArea", name: "Regio 's-Hertogenbosch" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Zakelijk", item: URL },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title="Zakelijke glasbewassing Rosmalen & Den Bosch | Kantoren, VvE's, winkels | Cleven Cleaning"
        description="Glazenwasser voor bedrijven, VvE's, winkels en scholen in Rosmalen en Den Bosch. Vaste planning, werken buiten openingstijden, verzamelfactuur. Vraag een offerte op locatie aan."
        canonicalUrl={URL}
        jsonLd={jsonLd}
      />

      <PageHero
        testId="business-hero"
        crumbs={[{ label: "Home", to: "/" }, { label: "Zakelijk" }]}
        badge="Voor bedrijven, VvE's en instellingen"
        title="Zakelijke glasbewassing & gevelonderhoud"
        intro="Een representatief pand zonder gedoe. Cleven Cleaning verzorgt glasbewassing, gevelreiniging en periodiek onderhoud voor kantoren, winkels, VvE's, scholen en zorginstellingen in Rosmalen, Den Bosch en omgeving — op een vast schema en met één aanspreekpunt."
        aside={
          <div id="offerte" className="bg-white rounded-3xl p-7 shadow-2xl" data-testid="business-callback-card">
            <h2 className="text-xl font-bold text-slate-800 mb-1">Offerte op locatie aanvragen</h2>
            <p className="text-sm text-slate-600 mb-5">Laat uw gegevens achter. Felix belt u terug om een vrijblijvende afspraak op locatie te maken.</p>
            <CallbackForm source="zakelijk" showCompany buttonText="Vraag offerte op locatie aan" />
          </div>
        }
      />

      {/* Voor wie */}
      <section className="py-24 bg-white" aria-labelledby="audience-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold mb-4">Voor wie</span>
            <h2 id="audience-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">Wij werken voor</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Van een enkele winkelpui tot een compleet appartementencomplex: wij stemmen frequentie, tijdstip en facturatie af op uw organisatie.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-gradient-to-br from-white to-slate-50 rounded-3xl p-7 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all border border-slate-100" data-testid={`audience-card-${title.split(" ")[0].toLowerCase()}`}>
                <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-teal-500 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-sky-500/20">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3>
                <p className="text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waarom */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-sky-50" aria-labelledby="reasons-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="reasons-heading" className="text-3xl sm:text-4xl font-bold text-slate-800 mb-12 text-center">
            Waarom bedrijven kiezen voor Cleven Cleaning
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reasons.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex gap-4 bg-white rounded-2xl p-6 shadow-md">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diensten */}
      <section className="py-24 bg-white" aria-labelledby="business-services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="business-services-heading" className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4 text-center">Diensten voor uw pand</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">Alles rondom het gebouw uit één hand, op één factuur.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {serviceIndex.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} data-testid={`business-service-link-${s.slug}`} className="group flex items-center justify-between gap-2 bg-gradient-to-br from-slate-50 to-sky-50 rounded-2xl p-4 border border-slate-100 hover:border-sky-300 hover:shadow-lg transition-all">
                <span className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors text-sm">{s.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="#offerte" className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-sky-500 to-teal-500 text-white font-semibold rounded-xl hover:from-sky-600 hover:to-teal-600 shadow-lg shadow-sky-500/25 transition-all" data-testid="business-offerte-cta">
              Offerte op locatie aanvragen
            </a>
            <p className="text-sm text-slate-500 mt-3">Of bel direct: <a href={`tel:${companyInfo.phoneClean}`} className="text-sky-600 font-semibold">{companyInfo.phone}</a></p>
          </div>
        </div>
      </section>

      <Reviews />
      <Contact />
    </>
  );
}
